import { useState, type FormEvent } from "react";
import { topicCategories } from "@/mocks/topics";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dah8m3rp14a1h3mr6fcg";

const materialOptions = ["文字说明", "截图 / 照片", "合同 / 文件", "录音 / 视频"];
const interviewOptions = ["可以配合采访", "仅提供材料", "需要先确认保护方案"];

export default function SubmissionForm() {
  const [summaryLen, setSummaryLen] = useState(0);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // 防垃圾字段：非空则静默按普通成功处理，不发送任何数据
    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      setSummaryLen(0);
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const val = typeof value === "string" ? value.trim() : "";
      if (val.length > 0) params.append(key, val);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpamSignal = /spam/i.test(serverMsg) || /form data is spam/i.test(responseText);
      const succeeded = response.ok && parsed?.code === "OK" && !isSpamSignal;

      if (succeeded) {
        setStatus("success");
        form.reset();
        setSummaryLen(0);
        return;
      }

      setStatus("error");
      setFormError(
        serverMsg && serverMsg !== "OK"
          ? serverMsg
          : "提交没有成功，请检查必填项后重试，或直接发送邮件给编辑。",
      );
    } catch {
      setStatus("error");
      setFormError("网络连接异常，提交未成功。请稍后重试，或改用邮箱投稿。");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-8 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-500 text-background-50">
          <i className="ri-check-line text-2xl leading-none"></i>
        </div>
        <h2 className="mt-5 font-heading text-xl font-bold text-foreground-950">线索已收到，感谢你的信任</h2>
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
          编辑会在工作日 24 小时内完成初审。如果线索需要补充材料，我们会通过你留下的联系方式与你联系。
          请勿在公开平台提前发布原始材料。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-6 inline-flex items-center gap-2 rounded-md border border-secondary-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-secondary-800 transition-colors hover:bg-secondary-100 cursor-pointer"
        >
          <i className="ri-add-line text-base leading-none"></i>
          再投一条线索
        </button>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="contact_name" className="block text-sm font-medium text-foreground-800">
            称呼 <span className="text-primary-600">*</span>
          </label>
          <input
            id="contact_name"
            name="contact_name"
            type="text"
            required
            placeholder="可用化名，如「陈先生」"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground-800">
            联系邮箱 <span className="text-primary-600">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            placeholder="用于接收初审答复"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-foreground-800">
            联系电话 <span className="text-foreground-500">（选填）</span>
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            placeholder="紧急线索可留手机号"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>

        <div>
          <label htmlFor="anonymous" className="block text-sm font-medium text-foreground-800">
            署名方式
          </label>
          <select
            id="anonymous"
            name="anonymous"
            defaultValue="可用化名"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <option value="可用化名">可用化名</option>
            <option value="完全匿名">完全匿名</option>
            <option value="可实名">可实名</option>
          </select>
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="category" className="block text-sm font-medium text-foreground-800">
            选题分类 <span className="text-primary-600">*</span>
          </label>
          <select
            id="category"
            name="category"
            required
            defaultValue=""
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <option value="" disabled>
              请选择最接近的分类
            </option>
            {topicCategories
              .filter((c) => c !== "全部选题")
              .map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
          </select>
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="title" className="block text-sm font-medium text-foreground-800">
            选题标题 <span className="text-primary-600">*</span>
          </label>
          <input
            id="title"
            name="title"
            type="text"
            required
            placeholder="一句话说明这件事，如「某小区加装电梯停工两年」"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>

        <div className="sm:col-span-2">
          <div className="flex items-center justify-between">
            <label htmlFor="summary" className="block text-sm font-medium text-foreground-800">
              线索概述 <span className="text-primary-600">*</span>
            </label>
            <span className={`text-xs ${summaryLen > 500 ? "text-primary-700" : "text-foreground-500"}`}>
              {summaryLen} / 500
            </span>
          </div>
          <textarea
            id="summary"
            name="summary"
            required
            maxLength={500}
            rows={6}
            onChange={(e) => setSummaryLen(e.target.value.length)}
            placeholder="按时间顺序讲清：发生了什么、涉及哪些人、你现在掌握了哪些材料。不需要文采，说清楚就好。"
            className="mt-2 w-full resize-y rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          ></textarea>
        </div>

        <fieldset className="sm:col-span-2">
          <legend className="text-sm font-medium text-foreground-800">你手上有哪些材料？</legend>
          <div className="mt-3 flex flex-wrap gap-2.5">
            {materialOptions.map((option) => (
              <label
                key={option}
                className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50 has-[:checked]:text-primary-700"
              >
                <input
                  type="checkbox"
                  name="materials"
                  value={option}
                  className="h-4 w-4 accent-[oklch(var(--primary-500))]"
                />
                {option}
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset className="sm:col-span-2">
          <legend className="text-sm font-medium text-foreground-800">后续配合方式</legend>
          <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-3">
            {interviewOptions.map((option, index) => (
              <label
                key={option}
                className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50 has-[:checked]:text-primary-700"
              >
                <input
                  type="radio"
                  name="interview"
                  value={option}
                  defaultChecked={index === 0}
                  className="h-4 w-4 accent-[oklch(var(--primary-500))]"
                />
                {option}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="sm:col-span-2">
          <label className="flex cursor-pointer items-start gap-2.5 rounded-md bg-background-100 p-4 text-sm leading-relaxed text-foreground-700">
            <input
              type="checkbox"
              name="consent"
              value="已阅读并同意"
              required
              className="mt-0.5 h-4 w-4 accent-[oklch(var(--primary-500))]"
            />
            我确认以上内容为本人真实提供，并同意编辑在保护个人信息的前提下进行核实与跟进。
          </label>
        </div>
      </div>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="word-submission-aux-field"
      />

      {status === "error" && (
        <p className="mt-5 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 p-3.5 text-sm leading-relaxed text-primary-800">
          <i className="ri-error-warning-line text-base leading-none"></i>
          {formError}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap cursor-pointer sm:w-auto"
      >
        <i className="ri-send-plane-fill text-base leading-none"></i>
        {status === "submitting" ? "正在提交..." : "提交线索"}
      </button>
      <p className="mt-3 text-xs leading-relaxed text-foreground-500">
        提交即代表你已阅读投稿须知。涉及敏感内容时，建议先通过邮箱与编辑确认保护方案。
      </p>
    </form>
  );
}